import { useEffect, useState } from "react";
import { useParams, Link } from "react-router-dom";
import { ArrowLeft, Phone, Mail, Car, ClipboardList, HandCoins, Landmark } from "lucide-react";
import api from "../lib/api";
import { useApp } from "../context/AppContext";
import { StatusBadge, TableShell, EmptyRow } from "../components/Shared";

const Stat = ({ icon: Icon, label, value, accent, testId }) => (
  <div data-testid={testId} className="bg-white border border-slate-200 rounded-lg p-5 shadow-sm">
    <div className="flex items-center justify-between mb-2">
      <span className="text-xs uppercase tracking-wider font-semibold text-slate-500">{label}</span>
      <Icon size={16} className={accent} strokeWidth={2} />
    </div>
    <div className="font-heading text-xl font-bold text-slate-900">{value}</div>
  </div>
);

export default function CustomerProfile() {
  const { id } = useParams();
  const { t, fmt } = useApp();
  const [data, setData] = useState(null);
  const [error, setError] = useState(false);

  useEffect(() => {
    api.get(`/customers/${id}/history`).then((r) => setData(r.data)).catch(() => setError(true));
  }, [id]);

  if (error) return <div className="text-slate-400">{t("noData")}</div>;
  if (!data) return <div className="text-slate-400">…</div>;
  const { customer: c, orders, payments, totals } = data;

  return (
    <div className="space-y-8">
      <div>
        <Link to="/customers" data-testid="back-to-customers-link" className="inline-flex items-center gap-2 text-sm text-sky-600 hover:text-sky-800 font-medium mb-3">
          <ArrowLeft size={15} /> {t("customers")}
        </Link>
        <h1 className="font-heading text-3xl sm:text-4xl tracking-tight font-bold text-slate-900" data-testid="customer-profile-name">{c.name}</h1>
        <div className="flex flex-wrap gap-x-6 gap-y-1 mt-2 text-sm text-slate-500">
          {c.phone && <span className="inline-flex items-center gap-1.5"><Phone size={14} /> {c.phone}</span>}
          {c.email && <span className="inline-flex items-center gap-1.5"><Mail size={14} /> {c.email}</span>}
          {(c.vehicles || []).map((v) => (
            <span key={v.id} className="inline-flex items-center gap-1.5 uppercase">
              <Car size={14} /> {[v.vehicle, v.year, v.color, v.plate].filter(Boolean).join(" · ")}
              {v.vin && <span className="text-slate-400"> · VIN {v.vin}</span>}
            </span>
          ))}
        </div>
      </div>
      <div className="grid grid-cols-2 lg:grid-cols-4 gap-4">
        <Stat testId="profile-orders-count" icon={ClipboardList} label={t("workOrders")} value={totals.orders} accent="text-sky-600" />
        <Stat testId="profile-billed" icon={Landmark} label={t("total")} value={fmt(totals.billed)} accent="text-slate-600" />
        <Stat testId="profile-paid" icon={HandCoins} label={t("paid")} value={fmt(totals.paid)} accent="text-emerald-600" />
        <Stat testId="profile-balance" icon={HandCoins} label={t("balance")} value={fmt(totals.balance)} accent="text-orange-500" />
      </div>
      <div>
        <h2 className="font-heading text-xl font-semibold text-slate-900 mb-4">{t("workOrders")}</h2>
        <TableShell headers={[t("order"), t("date"), t("total"), t("paid"), t("balance"), t("status")]}>
          {orders.length === 0 && <EmptyRow colSpan={6} />}
          {orders.map((o) => (
            <tr key={o.id} className="hover:bg-slate-50">
              <td className="px-4 py-3 font-semibold text-slate-900">{o.order_number}</td>
              <td className="px-4 py-3 text-slate-500">{(o.created_at || "").slice(0, 10)}</td>
              <td className="px-4 py-3 font-medium">{fmt(o.total)}</td>
              <td className="px-4 py-3">{fmt(o.paid_amount)}</td>
              <td className="px-4 py-3">{fmt(o.total - o.paid_amount)}</td>
              <td className="px-4 py-3"><StatusBadge status={o.status} /></td>
            </tr>
          ))}
        </TableShell>
      </div>
      <div>
        <h2 className="font-heading text-xl font-semibold text-slate-900 mb-4">{t("payments")}</h2>
        <TableShell headers={[t("date"), t("order"), t("method"), t("amount")]}>
          {payments.length === 0 && <EmptyRow colSpan={4} />}
          {payments.map((p) => (
            <tr key={p.id} className="hover:bg-slate-50">
              <td className="px-4 py-3 text-slate-500">{(p.date || "").slice(0, 10)}</td>
              <td className="px-4 py-3 font-medium">{p.order_number}</td>
              <td className="px-4 py-3 capitalize">{p.method ? t(p.method) : "—"}</td>
              <td className="px-4 py-3 font-semibold text-emerald-600">+{fmt(p.amount)}</td>
            </tr>
          ))}
        </TableShell>
      </div>
    </div>
  );
}
